import { useEffect, useMemo, useState } from "react";
import { Clock, RefreshCw, SlidersHorizontal, Sunrise, TrendingDown, TrendingUp, Users, Target, Gauge } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { dashboardViewFor } from "../lib/roles";
import { api, runGhlSync, fmtEuro, fmtNum, fmtErr } from "../lib/api";
import EmptyState from "../components/EmptyState";
import { SkeletonKpiGrid, SkeletonCard, SlowHint } from "../components/Skeleton";
import PeriodBar, { presetRange, PRESETS } from "../components/dashboard/PeriodBar";
import ChannelsWidget from "../components/dashboard/ChannelsWidget";
import CampaignsByClient from "../components/dashboard/CampaignsByClient";
import ClickUpWidget from "../components/clickup/ClickUpWidgets";
import KpiModal from "../components/dashboard/KpiModal";
import TeamQuality from "../components/dashboard/TeamQuality";
import StageTimes from "../components/dashboard/StageTimes";
import FunnelChart from "../components/dashboard/FunnelChart";
import ClosingTimeModal from "../components/ClosingTimeModal";


function KpiCard({ label, value, sub, icon: Icon, testid, i }) {
  return (
    <div data-testid={testid} className={`athanor-card min-w-0 p-5 fade-up fade-up-${Math.min(i + 1, 4)}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[13px] text-slate-400">{label}</span>
        {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-500" />}
      </div>
      <div className="mt-2 min-w-0 [overflow-wrap:anywhere] tabular-nums text-[clamp(22px,6.5vw,32px)] font-semibold leading-tight tracking-tight text-slate-50" data-testid={`${testid}-value`} title={typeof value === "string" ? value : undefined}>{value}</div>
      {sub && <div className="mt-1 line-clamp-2 text-xs text-slate-500" title={sub}>{sub}</div>}
    </div>
  );
}

function Expandable({ text, testid }) {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div className="min-w-0">
      <div className={`text-xs text-slate-500 ${open ? "" : "line-clamp-2"}`} data-testid={testid}>{text}</div>
      {text.length > 90 && <button data-inline onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }} className="mt-0.5 inline-flex min-h-11 items-center text-xs font-medium text-slate-300 hover:underline md:min-h-0">{open ? "Mostra meno" : "Mostra tutto"}</button>}
    </div>
  );
}

export default function Dashboard() {
  const saved = (() => { try { return JSON.parse(localStorage.getItem("athanor_dash_filters") || "{}"); } catch { return {}; } })();
  const initialPreset = saved.preset && PRESETS[saved.preset] ? saved.preset : "this_month";
  const [period, setPeriod] = useState(initialPreset === "custom" && saved.start && saved.end
    ? { preset: "custom", start: saved.start, end: saved.end } : { preset: initialPreset, ...presetRange(initialPreset) });
  const [channels, setChannels] = useState(null);
  const [pipelineId, setPipelineId] = useState(saved.pipelineId || "all");
  const [qualityPipeline, setQualityPipeline] = useState(saved.qualityPipeline || saved.pipelineId || "all");
  useEffect(() => {
    localStorage.setItem("athanor_dash_filters", JSON.stringify({ preset: period.preset, start: period.start, end: period.end, pipelineId, qualityPipeline }));
  }, [period, pipelineId, qualityPipeline]);
  const [pipelines, setPipelines] = useState([]);
  const [data, setData] = useState(null);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStep, setSyncStep] = useState("");
  const [kpiOpen, setKpiOpen] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);
  const { user: me } = useAuth();
  const [modules, setModules] = useState({ sales: true, marketing: true, secretary: true });
  const [clients, setClients] = useState(null);
  const forced = dashboardViewFor(me) || (!modules.sales ? "marketing" : (!modules.marketing ? "commerciale" : null));
  const [clickup, setClickup] = useState(null);
  useEffect(() => { api.get("/clickup/overview").then(({ data: d }) => setClickup(d)).catch(() => {}); }, []);
  const [view, setView] = useState(() => forced || localStorage.getItem("dashboard-view") || "commerciale");
  useEffect(() => { localStorage.setItem("dashboard-view", view); }, [view]);
  const [customKpis, setCustomKpis] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);

  const params = useMemo(() => ({ pipeline_id: view === "marketing" ? "all" : pipelineId, start: period.start, end: period.end, days: 30 }), [period, pipelineId, view]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: dash }, { data: pipes }, { data: st }] = await Promise.all([
        api.get("/dashboard", { params }), api.get("/pipelines"), api.get("/settings"),
      ]);
      setData(dash);
      setPipelines(pipes.pipelines.filter((p) => !p.hidden));
      setCustomKpis(st.custom_kpis || []);
      const mods = st.modules || { sales: true, marketing: true, secretary: true };
      setModules(mods);
      if (mods.marketing !== false) api.get("/meta/clients").then(({ data }) => setClients(data.connected ? data : null)).catch(() => setClients(null));
      if (!mods.sales) setView("marketing");
      if (!dash.connected && !st.onboarding_done && mods.sales) { window.location.assign("/onboarding"); return; }
      if (qualityPipeline === pipelineId) setQuality(dash.team_quality);
      if (dash.connected) api.get("/daily-brief").then(({ data: b }) => setBrief(b.available ? b : null)).catch(() => {});
      api.get("/channels", { params: { start: period.start, end: period.end, pipeline_id: pipelineId } }).then(({ data: c }) => setChannels(c)).catch(() => setChannels(null));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [params]);
  useEffect(() => {
    if (!data) return;
    if (qualityPipeline === pipelineId) { setQuality(data.team_quality); return; }
    api.get("/dashboard", { params: { ...params, pipeline_id: qualityPipeline } }).then(({ data: d }) => setQuality(d.team_quality)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qualityPipeline, data]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await runGhlSync(setSyncStep);
      toast.success(`Sincronizzazione completata: ${res.opportunities} opportunità, ${res.contacts} contatti aggiornati`);
      if (res.errors?.length) toast.warning(`Alcuni avvisi: ${res.errors.join(" · ")}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSyncing(false); setSyncStep(""); }
  };

  const saveKpis = async (next) => {
    setCustomKpis(next);
    try { await api.put("/settings", { custom_kpis: next }); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const recalcAll = async () => {
    try { const { data: res } = await api.post("/sellers/recalc-all"); toast.success(res.message); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const refreshBrief = async () => {
    setBriefLoading(true);
    try { const { data: b } = await api.post("/daily-brief/refresh"); setBrief(b.available ? b : null); } catch (e) { toast.error(fmtErr(e)); } finally { setBriefLoading(false); }
  };

  const booting = loading && !data;
  if (data && !data.connected && modules.sales) {
    return <EmptyState testid="dashboard-empty-ghl" title="Collega GoHighLevel per iniziare"
      description="Le metriche si popolano solo dopo aver collegato il tuo sub-account GHL e lanciato la prima sincronizzazione."
      ctaLabel="Vai alle Impostazioni" ctaTo="/impostazioni" />;
  }

  const kpis = data?.kpis || {};
  const stages = pipelines.filter((p) => pipelineId === "all" || p.ghl_id === pipelineId).flatMap((p) => p.stages.map((s) => ({ ...s, name: pipelineId === "all" && pipelines.length > 1 ? `${s.name} (${p.name})` : s.name })));
  const chiusura = data?.chiusura || {};
  const tpf = data?.tempo_per_fase || [];
  const companyAvg = tpf.length ? tpf.reduce((a, r) => a + r.avg_h * r.n, 0) / tpf.reduce((a, r) => a + r.n, 0) : null;
  const periodLabel = period.preset === "custom" ? `${new Date(period.start).toLocaleDateString("it-IT")} → ${new Date(period.end).toLocaleDateString("it-IT")}` : PRESETS[period.preset].toLowerCase();
  const funnels = (data?.funnel || []).filter((f) => f.stages.length && (pipelineId !== "all" || f.totale > 0));

  const marketing = view === "marketing";
  const multi = !!(clients && clients.clients.length > 1);
  const perClient = (fmt) => (clients ? clients.clients.map((c) => `${c.client_name}: ${fmt(c)}`).join(" · ") + " (ultimi 30 gg)" : "");
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-50">Dashboard</h1>
          <p className="mt-1 text-base text-slate-400">{marketing ? "Campagne, spesa e costo per lead." : "Funnel in tempo reale e performance dei venditori."}</p>
          {!forced && <div className="segmented mt-3" data-testid="dashboard-view-tabs">
            {[["commerciale", "Commerciale"], ["marketing", "Marketing"]].map(([id, label]) => (
              <button key={id} onClick={() => setView(id)} data-testid={`dashboard-tab-${id}`} data-inline
                className={view === id ? "seg-on" : ""}>{label}</button>
            ))}
          </div>}
        </div>
        {modules.sales && <button onClick={sync} disabled={syncing} data-testid="sync-ghl-button" className="btn btn-secondary">
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? (syncStep ? `Sincronizzo: ${syncStep}` : "Sincronizzo…") : "Sincronizza GHL"}
        </button>}
      </div>

      <PeriodBar period={period} setPeriod={setPeriod} pipelineId={pipelineId} setPipelineId={setPipelineId} pipelines={pipelines} hidePipeline={marketing || !modules.sales} />

      {booting ? (
        <div className="space-y-6" data-testid="dashboard-skeleton">
          <SkeletonKpiGrid n={4} />
          <SkeletonCard rows={5} />
          <SkeletonCard chart />
          <SlowHint />
        </div>
      ) : marketing ? (
        <div className="space-y-6" data-testid="dashboard-marketing">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
            <KpiCard i={0} testid="mkt-kpi-spesa" label={multi ? "Spesa Meta Ads · tutti i clienti" : "Spesa Meta Ads"} value={fmtEuro(kpis.spesa_ads)}
              sub={!kpis.meta_connected ? "Richiede Meta Ads collegato" : multi ? perClient((c) => fmtEuro(c.spend_30)) : `Periodo: ${periodLabel}`} icon={Target} />
            <KpiCard i={1} testid="mkt-kpi-lead-ads" label={multi ? "Lead da Meta · tutti i clienti" : "Lead da Meta"} value={fmtNum(kpis.lead_ads)}
              sub={multi ? perClient((c) => fmtNum(c.leads_30)) : `Lead dichiarati dalle campagne · ${periodLabel}`} icon={Users} />
            <KpiCard i={2} testid="mkt-kpi-cpl" label={multi ? "Costo per lead · media clienti" : "Costo per lead"} value={fmtEuro(kpis.costo_per_lead)}
              sub={multi ? perClient((c) => (c.cpl_30 == null ? "—" : fmtEuro(c.cpl_30))) : "Spesa ÷ lead Meta"} icon={Gauge} />
            {modules.sales ? <KpiCard i={3} testid="mkt-kpi-cpo" label="Costo per opportunità CRM" value={fmtEuro(kpis.costo_per_opportunita)} sub={`${fmtNum(kpis.total_opportunita)} opportunità nel CRM`} icon={Clock} />
              : clients && <KpiCard i={3} testid="mkt-kpi-budget" label={`Spesa del mese · ${clients.clients.length} client${clients.clients.length === 1 ? "e" : "i"}`} value={fmtEuro(clients.totals.spend_month)}
                sub={clients.totals.budget ? `Budget totale ${fmtEuro(clients.totals.budget)} · usato ${Math.round(clients.totals.spend_month / clients.totals.budget * 100)}%` : "Imposta i budget in Meta Ads → Account seguiti"} icon={Clock} />}
          </div>
          <CampaignsByClient />
          <ClickUpWidget data={clickup} />
          <ChannelsWidget data={channels} />
        </div>
      ) : (
      <>
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span className="text-xs text-slate-500">Indicatori</span>
          <button onClick={() => setKpiOpen(true)} data-testid="customize-kpi-button"
            className="inline-flex items-center gap-2 rounded-md border border-zinc-700 px-3 py-1.5 text-xs text-slate-200 hover:border-[#C5A059]/50">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Personalizza KPI
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
          <KpiCard i={0} testid="kpi-card-total_opportunita" label="Opportunità totali" value={fmtNum(kpis.total_opportunita)} sub="Nel periodo selezionato" icon={Users} />
          <KpiCard i={1} testid="kpi-card-costo_per_lead" label="Costo per lead (Meta)" value={fmtEuro(kpis.costo_per_lead)} icon={Target}
            sub={kpis.meta_connected ? `${fmtEuro(kpis.spesa_ads)} spesi ÷ ${kpis.lead_ads ?? 0} lead Meta · €${kpis.costo_per_opportunita ?? "N/D"} per opportunità CRM (${kpis.total_opportunita})` : "Richiede Meta Ads collegato"} />
          {(kpis.custom_kpis || []).map((k, i) => (
            <KpiCard key={k.id} i={i + 2} testid={`kpi-card-custom-${k.id}`} label={k.name} value={fmtEuro(k.value)} icon={Gauge}
              sub={`${k.count} in "${k.stage}"${!kpis.meta_connected ? " · richiede Meta Ads" : ""}`} />
          ))}
        </div>
      </div>

      {quality && (
        <TeamQuality quality={quality} pipelines={pipelines} pipelineId={qualityPipeline} setPipelineId={setQualityPipeline} onRecalc={recalcAll} periodLabel={periodLabel} />
      )}

      <div className="athanor-card p-6 fade-up cursor-pointer transition-colors hover:border-[#C5A059]/40" data-testid="closing-time-card" onClick={() => setClosingOpen(true)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setClosingOpen(true)}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#C5A059]/30 bg-[#C5A059]/10"><Clock className="h-4 w-4 text-[#C5A059]" /></span>
            <div>
              <div className="text-xs text-slate-400">Tempo medio di chiusura <span className="text-slate-600">· clicca per il dettaglio</span></div>
              <div className="tabular-nums text-2xl font-bold text-slate-50" data-testid="closing-time-value">{chiusura.avg_days == null ? "—" : `${chiusura.avg_days} giorni`}</div>
            </div>
          </div>
          {chiusura.trend_pct != null && (
            <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${chiusura.trend_pct <= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`} data-testid="closing-time-trend">
              {chiusura.trend_pct <= 0 ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}{Math.abs(chiusura.trend_pct)}%
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-zinc-800/70 pt-3 text-xs text-slate-500">
          <span>{chiusura.n || 0} contratti chiusi nel periodo</span>
          <button type="button" onClick={(e) => { e.stopPropagation(); setClosingOpen(true); }} data-testid="closing-time-details-btn" className="-my-3 inline-flex min-h-11 items-center text-slate-300 hover:underline">Vedi chi ha chiuso →</button>
        </div>
      </div>
      {closingOpen && <ClosingTimeModal params={{ start: period.start, end: period.end, pipeline_id: pipelineId }} onClose={() => setClosingOpen(false)} />}

      <ChannelsWidget data={channels} />

      <StageTimes rows={tpf} series={data?.tempo_serie || []} companyAvg={companyAvg} />

      <div className="athanor-card p-6 fade-up fade-up-4" data-testid="funnel-card">
        <h2 className="mb-4 text-base font-semibold text-slate-100">Funnel Pipeline</h2>
        {funnels.length === 0 ? (
          <EmptyState compact testid="funnel-empty" title="Nessuna opportunità nel periodo per questa pipeline" ctaLabel="Cambia periodo" onCta={() => document.querySelector('[data-testid="filter-period"]')?.focus()} />
        ) : funnels.map((f) => (
          <div key={f.pipeline_id} className="mb-8 last:mb-0">
            {funnels.length > 1 && <div className="mb-2 text-sm font-medium text-slate-300">{f.pipeline}</div>}
            <FunnelChart funnel={f} />
            <div className="mt-4 border-t border-zinc-800/70 pt-3" data-testid={`lost-leads-${f.pipeline_id}`}>
              <div className="text-xs font-medium text-slate-300">Lead persi</div>
              <div className="mt-1.5 flex flex-wrap gap-4 text-xs text-slate-400">
                {f.persi_per_stage?.length ? f.persi_per_stage.map((l) => (
                  <span key={l.stage} className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-red-400" />{l.stage}: <span className="tabular-nums text-slate-200">{l.count}</span></span>
                )) : <span className="text-slate-500">Nessun lead perso nel periodo</span>}
              </div>
            </div>
          </div>
        ))}
        {data?.webhook_events === 0 && (
          <p className="mt-4 text-center text-xs text-slate-600" data-testid="webhook-pending-banner">Le metriche live si attivano quando GHL inizia a inviare eventi OpportunityStageUpdate.</p>
        )}
      </div>

      {brief && (
        <div className="athanor-card p-6 fade-up" data-testid="daily-brief-card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-100"><Sunrise className="h-4 w-4 text-[#C5A059]" /> Riepilogo del giorno</h2>
            <button onClick={refreshBrief} disabled={briefLoading} data-testid="refresh-brief-button" className="inline-flex items-center gap-1.5 text-xs text-[#C5A059] hover:underline disabled:opacity-50">
              <RefreshCw className={`h-3 w-3 ${briefLoading ? "animate-spin" : ""}`} /> Aggiorna
            </button>
          </div>
          <p className="text-sm leading-relaxed text-slate-300" data-testid="daily-brief-text">{brief.riepilogo}</p>
          {brief.azioni?.length > 0 && (
            <div className="mt-4 space-y-2">
              {brief.azioni.map((a, i) => (
                <a key={`${a.titolo}-${i}`} href={`/${a.sezione || ""}`} data-testid={`brief-action-${i}`} className="flex items-center gap-3 rounded-lg border border-zinc-800/60 px-4 py-2.5 hover:border-[#C5A059]/40">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#C5A059]/30 tabular-nums text-xs text-[#C5A059]">{i + 1}</span>
                  <div className="min-w-0 flex-1"><div className="text-sm text-slate-100">{a.titolo}</div><Expandable text={a.dettaglio} testid={`brief-action-detail-${i}`} /></div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      </>
      )}

      {kpiOpen && <KpiModal kpis={customKpis} stages={stages} onChange={saveKpis} onClose={() => setKpiOpen(false)} />}
    </div>
  );
}
